import { useEffect, useMemo, useState } from "react";
import Card from "../../components/common/Card";
import Button from "../../components/common/Button";
import Checkbox from "../../components/common/Checkbox";
import Input from "../../components/common/Input";
import Alert from "../../components/common/Alert";
import PageHeader from "../../components/common/PageHeader";
import Section from "../../components/common/Section";
import { useStore } from "../../context/StoreContext";
import {
  useGetBusinessGeneralSettingsQuery,
  useGetBusinessTipPresetsQuery,
  useGetStoreFeaturesQuery,
  useUpdateBusinessGeneralSettingsMutation,
  useUpdateBusinessTipPresetsMutation,
} from "../../store/api";

const createPresetRow = (preset = {}, index = 0) => ({
  rowId: `${Date.now()}-${index}-${Math.random().toString(36).slice(2)}`,
  percent:
    preset.percent === null || preset.percent === undefined
      ? ""
      : String(preset.percent),
  displayOrder:
    preset.displayOrder === null || preset.displayOrder === undefined
      ? String(index)
      : String(preset.displayOrder),
  isActive: preset.isActive === undefined ? true : Boolean(preset.isActive),
});

const getTipPresetsPayload = (rows) => ({
  presets: rows.map((row) => ({
    percent: Number(row.percent),
    displayOrder: Number(row.displayOrder),
    isActive: Boolean(row.isActive),
  })),
});

const GeneralSettings = () => {
  const { selectedStoreId } = useStore();
  const {
    data: settings,
    isLoading,
    isError,
    error,
  } = useGetBusinessGeneralSettingsQuery();
  const {
    data: tipPresets,
    isLoading: isTipPresetsLoading,
    isError: isTipPresetsError,
    error: tipPresetsError,
  } = useGetBusinessTipPresetsQuery();
  const { data: storeFeatures } = useGetStoreFeaturesQuery(selectedStoreId, {
    skip: !selectedStoreId,
  });
  const [updateSettings, updateState] =
    useUpdateBusinessGeneralSettingsMutation();
  const [updateTipPresets, updateTipPresetsState] =
    useUpdateBusinessTipPresetsMutation();

  const [form, setForm] = useState({
    requireCustomerDetails: false,
    showAllergens: false,
    showDietTags: false,
    allowCustomTip: true,
  });
  const [presetRows, setPresetRows] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (settings) {
      setForm({
        requireCustomerDetails: Boolean(settings.requireCustomerDetails),
        showAllergens: Boolean(settings.showAllergens),
        showDietTags: Boolean(settings.showDietTags),
        allowCustomTip:
          settings.allowCustomTip === undefined
            ? true
            : Boolean(settings.allowCustomTip),
      });
    }
  }, [settings]);

  useEffect(() => {
    if (tipPresets?.presets) {
      const rows = [...tipPresets.presets]
        .sort((left, right) => left.displayOrder - right.displayOrder)
        .map((preset, index) => createPresetRow(preset, index));
      setPresetRows(rows);
    }
  }, [tipPresets]);

  const showKioskVisibility = storeFeatures
    ? Boolean(storeFeatures?.enableKiosk)
    : true;

  const presetErrors = useMemo(() => {
    return presetRows.reduce((errors, row) => {
      const rowErrors = {};
      const percent = Number(row.percent);
      const displayOrder = Number(row.displayOrder);

      if (row.percent === "" || Number.isNaN(percent) || percent <= 0) {
        rowErrors.percent = "Percentage must be greater than zero.";
      }

      if (
        row.displayOrder === "" ||
        Number.isNaN(displayOrder) ||
        displayOrder < 0 ||
        !Number.isInteger(displayOrder)
      ) {
        rowErrors.displayOrder = "Enter a whole number of 0 or more.";
      }

      if (Object.keys(rowErrors).length > 0) {
        errors[row.rowId] = rowErrors;
      }

      return errors;
    }, {});
  }, [presetRows]);

  const hasPresetErrors = Object.keys(presetErrors).length > 0;

  const handleChange = (event) => {
    const { name, checked } = event.target;
    setForm((prev) => ({ ...prev, [name]: checked }));
    setSuccessMessage("");
  };

  const handlePresetChange = (rowId, field, value) => {
    setPresetRows((prev) =>
      prev.map((row) =>
        row.rowId === rowId ? { ...row, [field]: value } : row,
      ),
    );
    setSuccessMessage("");
  };

  const handleAddPreset = () => {
    const nextDisplayOrder =
      presetRows.length === 0
        ? 0
        : Math.max(
            ...presetRows.map((row) => {
              const value = Number(row.displayOrder);
              return Number.isNaN(value) ? -1 : value;
            }),
          ) + 1;

    setPresetRows((prev) => [
      ...prev,
      createPresetRow({ percent: "", displayOrder: nextDisplayOrder }, prev.length),
    ]);
    setSuccessMessage("");
  };

  const handleRemovePreset = (rowId) => {
    setPresetRows((prev) => prev.filter((row) => row.rowId !== rowId));
    setSuccessMessage("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccessMessage("");

    if (form.allowCustomTip && hasPresetErrors) {
      return;
    }

    try {
      await updateSettings({
        requireCustomerDetails: Boolean(form.requireCustomerDetails),
        showAllergens: Boolean(form.showAllergens),
        showDietTags: Boolean(form.showDietTags),
        allowCustomTip: Boolean(form.allowCustomTip),
      }).unwrap();

      if (form.allowCustomTip) {
        await updateTipPresets(getTipPresetsPayload(presetRows)).unwrap();
      }

      setSuccessMessage("General settings updated.");
    } catch (err) {
      // error handled by UI
    }
  };

  const tipPresetsSection = (
    <Section>
      <div className="space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">
              Tip Presets
            </h2>
            <p className="text-sm text-slate-600">
              Configure the business-wide percentage options shown to guests.
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={handleAddPreset}>
            Add Tip
          </Button>
        </div>

        {isTipPresetsLoading && (
          <Alert tone="info">Loading tip presets...</Alert>
        )}
        {isTipPresetsError && (
          <Alert tone="error">
            {tipPresetsError?.data?.detail || "Failed to load tip presets."}
          </Alert>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
            <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Percent</th>
                <th className="px-4 py-3">Display Order</th>
                <th className="px-4 py-3">Active</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {presetRows.length === 0 ? (
                <tr>
                  <td
                    className="px-4 py-5 text-center text-slate-500"
                    colSpan={4}
                  >
                    No tip presets configured.
                  </td>
                </tr>
              ) : (
                presetRows.map((row) => (
                  <tr key={row.rowId} className="align-top">
                    <td className="min-w-48 px-4 py-3">
                      <Input
                        aria-label="Tip percent"
                        type="number"
                        value={row.percent}
                        onChange={(event) =>
                          handlePresetChange(
                            row.rowId,
                            "percent",
                            event.target.value,
                          )
                        }
                        error={presetErrors[row.rowId]?.percent}
                        rightElement={
                          <span className="text-sm font-semibold text-slate-500">
                            %
                          </span>
                        }
                      />
                    </td>
                    <td className="min-w-44 px-4 py-3">
                      <Input
                        aria-label="Tip display order"
                        type="number"
                        value={row.displayOrder}
                        onChange={(event) =>
                          handlePresetChange(
                            row.rowId,
                            "displayOrder",
                            event.target.value,
                          )
                        }
                        error={presetErrors[row.rowId]?.displayOrder}
                      />
                    </td>
                    <td className="px-4 py-5">
                      <Checkbox
                        label="Active"
                        checked={row.isActive}
                        onChange={(event) =>
                          handlePresetChange(
                            row.rowId,
                            "isActive",
                            event.target.checked,
                          )
                        }
                      />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePreset(row.rowId)}
                      >
                        Remove
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {updateTipPresetsState.isError && (
          <Alert tone="error">
            {updateTipPresetsState.error?.data?.detail ||
              "Failed to update tip presets."}
          </Alert>
        )}

      </div>
    </Section>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="General Settings"
        subtitle="Configure business-wide preferences for ordering behavior."
      />

      <Card>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <Section
            title="Customer Details"
            subtitle="Apply to all stores under this business."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Checkbox
                label="Require customer details"
                name="requireCustomerDetails"
                checked={form.requireCustomerDetails}
                onChange={handleChange}
              />
            </div>
          </Section>

          {showKioskVisibility && (
            <Section
              title="Kiosk Visibility"
              subtitle="Control which details appear on kiosk ordering."
            >
              <div className="grid gap-4 md:grid-cols-2">
                <Checkbox
                  label="Show Allergens on Kiosk"
                  name="showAllergens"
                  checked={form.showAllergens}
                  onChange={handleChange}
                />
                <Checkbox
                  label="Show Diet Tags on Kiosk"
                  name="showDietTags"
                  checked={form.showDietTags}
                  onChange={handleChange}
                />
              </div>
            </Section>
          )}

          <Section
            title="Tip Options"
            subtitle="Control whether guests can enter a tip outside the saved presets."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <Checkbox
                label="Allow guests to enter a custom tip amount"
                name="allowCustomTip"
                checked={form.allowCustomTip}
                onChange={handleChange}
              />
            </div>
          </Section>

          {form.allowCustomTip && tipPresetsSection}

          {isLoading && <Alert tone="info">Loading current settings...</Alert>}
          {isError && (
            <Alert tone="error">
              {error?.data?.detail || "Failed to load general settings."}
            </Alert>
          )}
          {successMessage && <Alert tone="success">{successMessage}</Alert>}
          {updateState.isError && (
            <Alert tone="error">
              {updateState.error?.data?.detail ||
                "Failed to update general settings."}
            </Alert>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={
                updateState.isLoading ||
                updateTipPresetsState.isLoading ||
                (form.allowCustomTip && hasPresetErrors)
              }
            >
              {updateState.isLoading || updateTipPresetsState.isLoading
                ? "Saving..."
                : "Save Settings"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default GeneralSettings;
